import React, { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useToast } from "@/hooks/use-toast";
import SmsTemplateManager from "@/components/SmsTemplateManager";
import NotificationSettings from "@/components/NotificationSettings";
import TwilioSettings from "@/components/TwilioSettings";
import SmsProviderSelector from "@/components/SmsProviderSelector";
import RingCentralSettings from "@/components/RingCentralSettings";
import {
  StoreProfileCard,
  SendingRulesCard,
  ActivityWindowCard,
  ComplianceCard,
  LinksCard,
} from "@/components/marketing/TextMarketingSettings";
import EmergencyStopCard from "@/components/marketing/EmergencyStopCard";
import KillSwitchBanner from "@/components/marketing/KillSwitchBanner";
import SettingsChipIndex, { type SettingsSectionRef } from "@/components/settings/SettingsChipIndex";
import { SendOffersToAllRow } from "@/components/marketing/SendOffersToAllRow";
import { PinEntryDialog } from "@/components/PinEntryDialog";
import { useNotificationSettings } from "@/hooks/useNotificationSettings";
import { useSmsProviders } from "@/hooks/useSmsProviders";
import type { SmsTemplate } from "@/hooks/useSmsTemplates";
import {
  MessageSquare,
  Send,
  Star,
  ChevronDown,
  Ruler,
  Pencil,
  LucideIcon,
} from "lucide-react";

/* A settings section: an anchor + clear header + generous separation, so an
   owner scans (or jumps via the chip index) to the group they want instead of
   reading a wall of switches. The scroll-margin keeps the heading clear of the
   sticky chip bar when jumped to. */
export const SettingsSection = ({
  id,
  title,
  description,
  children,
}: {
  id: string;
  title: string;
  description: string;
  children: React.ReactNode;
}) => (
  <section id={id} className="scroll-mt-24 space-y-4">
    <div className="max-w-prose">
      <h2 className="text-xl font-semibold text-foreground">{title}</h2>
      <p className="mt-1 text-sm text-muted-foreground">{description}</p>
    </div>
    {children}
  </section>
);

/* One automatic order text: icon + plain-language title + a visible explanation
   (never hidden behind hover), its automation switch when it has one, and an
   inline "Edit message" affordance that reveals the template editor in place —
   so what a text says sits next to the toggle that sends it. */
const AutomaticTextRow = ({
  id,
  icon: Icon,
  title,
  description,
  templateType,
  templateTitle,
  templateDescription,
  toggle,
}: {
  id: string;
  icon: LucideIcon;
  title: string;
  description: string;
  templateType: SmsTemplate["template_type"];
  templateTitle: string;
  templateDescription: string;
  toggle?: {
    label: string;
    description: string;
    checked: boolean;
    onCheckedChange: (checked: boolean) => void;
  };
}) => {
  const [editOpen, setEditOpen] = useState(false);

  return (
    <div className="py-4 first:pt-0 last:pb-0">
      <div className="flex items-start justify-between gap-4">
        <div className="flex min-w-0 items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
            <Icon className="h-[18px] w-[18px]" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-foreground">{title}</p>
            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{description}</p>
          </div>
        </div>
      </div>

      {toggle && (
        <div className="mt-3 flex items-start justify-between gap-4 pl-12">
          <div className="min-w-0">
            <Label id={`${id}-label`} htmlFor={id} className="text-sm font-medium text-foreground">
              {toggle.label}
            </Label>
            <p id={`${id}-desc`} className="mt-1 text-xs leading-relaxed text-muted-foreground">
              {toggle.description}
            </p>
          </div>
          <Switch
            id={id}
            checked={toggle.checked}
            onCheckedChange={toggle.onCheckedChange}
            aria-labelledby={`${id}-label`}
            aria-describedby={`${id}-desc`}
          />
        </div>
      )}

      <Collapsible open={editOpen} onOpenChange={setEditOpen}>
        <div className="mt-3 pl-12">
          <CollapsibleTrigger asChild>
            <Button variant="outline" size="sm" className="h-9 gap-1.5">
              <Pencil className="h-3.5 w-3.5" />
              Edit message
              <ChevronDown className={`h-3.5 w-3.5 transition-transform ${editOpen ? "rotate-180" : ""}`} />
            </Button>
          </CollapsibleTrigger>
        </div>
        <CollapsibleContent className="pt-4">
          <SmsTemplateManager
            templateType={templateType}
            title={templateTitle}
            description={templateDescription}
          />
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};

const SECTIONS: SettingsSectionRef[] = [
  { id: "delivery", label: "Delivery" },
  { id: "automatic-texts", label: "Automatic texts" },
  { id: "marketing-rules", label: "Marketing rules" },
  { id: "compliance-links", label: "Compliance & links" },
  { id: "diagnostics", label: "Diagnostics" },
];

const Settings = () => {
  const [isPinVerified, setIsPinVerified] = useState(false);
  const [showPinDialog, setShowPinDialog] = useState(true); // Manages dialog visibility explicitly
  const [guidelinesOpen, setGuidelinesOpen] = useState(false);
  const { toast } = useToast();
  const { settings: notificationSettings, updateSettings: updateNotificationSettings } = useNotificationSettings();
  const { providers, loading: providersLoading } = useSmsProviders();
  const activeProvider = providers.find(p => p.is_active);

  const PIN_VERIFIED_KEY = "settingsPinVerified";
  const PIN_TIMESTAMP_KEY = "settingsPinTimestamp";
  const TIMEOUT_DURATION = 15 * 60 * 1000; // 15 minutes in milliseconds

  const resetPinVerification = () => {
    setIsPinVerified(false);
    setShowPinDialog(true);
    sessionStorage.removeItem(PIN_VERIFIED_KEY);
    sessionStorage.removeItem(PIN_TIMESTAMP_KEY);
    // Optionally, inform the user why they are being asked for PIN again
    // toast({ title: "Session Expired", description: "Please enter PIN again." });
  };

  const updatePinTimestamp = React.useCallback(() => {
    if (sessionStorage.getItem(PIN_VERIFIED_KEY) === "true") {
      sessionStorage.setItem(PIN_TIMESTAMP_KEY, Date.now().toString());
    }
  }, []);

  useEffect(() => {
    const checkPinStatus = () => {
      const sessionPinVerified = sessionStorage.getItem(PIN_VERIFIED_KEY);
      const pinTimestamp = sessionStorage.getItem(PIN_TIMESTAMP_KEY);

      if (sessionPinVerified === "true" && pinTimestamp) {
        const lastVerifiedTime = parseInt(pinTimestamp, 10);
        if (Date.now() - lastVerifiedTime < TIMEOUT_DURATION) {
          setIsPinVerified(true);
          setShowPinDialog(false);
          updatePinTimestamp(); // Update timestamp on initial load/check if still valid
        } else {
          resetPinVerification(); // Timeout expired
          toast({ title: "Session Expired", description: "Please enter PIN to continue." });
        }
      } else {
        // Not verified or no timestamp, ensure dialog is shown
        setIsPinVerified(false);
        setShowPinDialog(true);
      }
    };

    checkPinStatus(); // Check on mount

    // Add event listener for tab visibility change
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkPinStatus();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Activity listeners - these should only be active if PIN is verified
    // and should update the timestamp
    const activityEvents = ['mousemove', 'keydown', 'scroll', 'click'];
    if (isPinVerified) {
      activityEvents.forEach(event => {
        window.addEventListener(event, updatePinTimestamp);
      });
    }

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      activityEvents.forEach(event => {
        window.removeEventListener(event, updatePinTimestamp);
      });
    };
  }, [isPinVerified, updatePinTimestamp, TIMEOUT_DURATION, toast]); // Rerun if isPinVerified changes to add/remove activity listeners

  const handlePinVerified = () => {
    setIsPinVerified(true);
    setShowPinDialog(false);
    sessionStorage.setItem(PIN_VERIFIED_KEY, "true");
    sessionStorage.setItem(PIN_TIMESTAMP_KEY, Date.now().toString());
    toast({
      title: "PIN Verified",
      description: "You can now access the settings.",
    });
  };

  if (!isPinVerified) {
    return (
      <PinEntryDialog
        open={showPinDialog}
        onPinVerified={handlePinVerified}
        onOpenChange={(open) => {
          if (!open && !isPinVerified) {
            // If dialog is closed without verification, keep it technically "open"
            // but rely on isPinVerified to control rendering.
            // This prevents users from simply dismissing the dialog.
            // A more robust solution might involve navigating away or disabling interaction.
            setShowPinDialog(true);
          } else {
            setShowPinDialog(open);
          }
        }}
      />
    );
  }

  return (
    <div className="pb-4">
      <header className="mb-6 flex flex-col gap-4 sm:mb-8 sm:flex-row sm:items-start sm:justify-between">
        <div className="max-w-prose">
          <h1 className="text-2xl font-bold text-foreground sm:text-3xl">Settings</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Set up how the portal texts your customers.
          </p>
        </div>
        {/* The one control that must be findable in a panic — pinned to the header,
            not buried at the bottom of the scroll. */}
        <EmergencyStopCard className="w-full sm:w-80 sm:shrink-0" />
      </header>

      <KillSwitchBanner />

      <SettingsChipIndex sections={SECTIONS} />

      <div className="mt-6 space-y-12">
        {/* 1. Delivery — how messages leave the building, plus the store profile
            those messages carry. */}
        <SettingsSection
          id="delivery"
          title="Delivery"
          description="How notifications reach customers: the store profile they carry, the sending mode, the provider, and its credentials."
        >
          <StoreProfileCard />
          <NotificationSettings />
          <SmsProviderSelector />
          {/* Show the active provider's form; for a fresh store with no provider
              active yet, show both so the setup form is always reachable. */}
          {activeProvider?.provider_name === 'twilio' && <TwilioSettings />}
          {activeProvider?.provider_name === 'ringcentral' && <RingCentralSettings />}
          {!providersLoading && !activeProvider && (
            <>
              <TwilioSettings />
              <RingCentralSettings />
            </>
          )}
        </SettingsSection>

        {/* 2. Automatic order texts — what goes out on its own, with each text's
            wording editable right beside the toggle that sends it. */}
        <SettingsSection
          id="automatic-texts"
          title="Automatic order texts"
          description="The texts tied to an order's lifecycle. Turn on the ones that send on their own, and edit what each one says in place."
        >
          <Card>
            <CardContent className="divide-y divide-border p-4 sm:p-6">
              <AutomaticTextRow
                id="auto-confirm-sms"
                icon={MessageSquare}
                title="Order confirmation"
                description="The text a customer gets when their order is confirmed. You can add promotional content for subscribed customers."
                templateType="order_confirmation"
                templateTitle="Order confirmation"
                templateDescription="Sent when an order is confirmed. You can add promotional content for subscribed customers."
                toggle={{
                  label: "Send confirmations automatically",
                  description: "Text a confirmation the moment an order comes in, without anyone tapping Confirm.",
                  checked: notificationSettings?.auto_confirm_enabled ?? false,
                  onCheckedChange: (checked) => updateNotificationSettings({ auto_confirm_enabled: checked }),
                }}
              />
              <AutomaticTextRow
                id="order-ready-sms"
                icon={Send}
                title="Order ready"
                description="The text that tells a customer their order is ready for pickup. Sent from the order when you mark it ready."
                templateType="order_ready"
                templateTitle="Order ready"
                templateDescription="Sent to tell a customer their order is ready for pickup."
              />
              <AutomaticTextRow
                id="auto-review-sms"
                icon={Star}
                title="Review request"
                description="The text that asks a customer to leave a review after an order is completed."
                templateType="ask_for_reviews"
                templateTitle="Review request"
                templateDescription="Sent after an order to ask the customer to leave a review."
                toggle={{
                  label: "Ask for a review automatically",
                  description: "Text a review request about an hour after an order is completed.",
                  checked: notificationSettings?.auto_review_enabled ?? false,
                  onCheckedChange: (checked) => updateNotificationSettings({ auto_review_enabled: checked }),
                }}
              />
              <div className="py-4 last:pb-0">
                <SendOffersToAllRow
                  checked={notificationSettings?.send_offers_to_all ?? false}
                  onConfirm={(value) => updateNotificationSettings({ send_offers_to_all: value })}
                />
              </div>
            </CardContent>
          </Card>
        </SettingsSection>

        {/* 3. Marketing rules — quiet hours, sending limits, and the activity window. */}
        <SettingsSection
          id="marketing-rules"
          title="Marketing rules"
          description="Quiet hours, how many texts a customer can get and how fast, and how recently someone must have ordered to count as active."
        >
          <SendingRulesCard />
          <ActivityWindowCard />
        </SettingsSection>

        {/* 4. Compliance & links — the wording customers see, and the domain used
            in every text. */}
        <SettingsSection
          id="compliance-links"
          title="Compliance & links"
          description="The 10DLC campaign id, the exact consent and opt-out wording customers see, and the links used in your texts."
        >
          <ComplianceCard />
          <LinksCard />
        </SettingsSection>

        {/* 5. Diagnostics — deliberately quiet, at the foot of the page. */}
        <SettingsSection
          id="diagnostics"
          title="Diagnostics"
          description="Reference and monitoring tools. You will not normally need these."
        >
          <Card>
            <CardContent className="flex flex-col justify-between gap-3 p-4 sm:flex-row sm:items-center sm:p-6">
              <div className="max-w-prose">
                <p className="text-sm font-medium text-foreground">Send a test error</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Triggers a sample error so we can confirm Sentry monitoring is reporting correctly.
                </p>
              </div>
              <Button
                variant="outline"
                className="h-11 shrink-0 border-status-danger/40 text-status-danger hover:bg-status-danger/10 hover:text-status-danger sm:h-10"
                onClick={() => { throw new Error("Sentry Test Error from Settings Page"); }}
              >
                Send test error
              </Button>
            </CardContent>
          </Card>

          {/* Reference, tucked away until needed */}
          <Collapsible open={guidelinesOpen} onOpenChange={setGuidelinesOpen}>
            <Card>
              <CollapsibleTrigger asChild>
                <button
                  type="button"
                  className="flex w-full items-center justify-between gap-4 p-4 text-left transition-colors hover:bg-muted/50 sm:p-6"
                >
                  <div className="flex items-center gap-3">
                    <Ruler className="h-5 w-5 shrink-0 text-muted-foreground" />
                    <div>
                      <p className="font-medium text-foreground">Length and segment guide</p>
                      <p className="text-sm text-muted-foreground">How many characters fit before a text splits.</p>
                    </div>
                  </div>
                  <ChevronDown className={`h-5 w-5 shrink-0 text-muted-foreground transition-transform duration-200 ${guidelinesOpen ? "rotate-180" : ""}`} />
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="space-y-3 border-t border-border p-4 text-sm sm:p-6">
                  <ul className="space-y-1.5 text-muted-foreground">
                    <li className="flex items-baseline gap-2">
                      <span className="font-medium text-status-success">Standard</span>
                      <span>Up to 160 characters send as a single text.</span>
                    </li>
                    <li className="flex items-baseline gap-2">
                      <span className="font-medium text-status-warning">Longer</span>
                      <span>161 to 1600 characters split into multiple segments.</span>
                    </li>
                    <li className="flex items-baseline gap-2">
                      <span className="font-medium text-status-danger">Watch out</span>
                      <span>Special characters cut the limit and may cost more to send.</span>
                    </li>
                  </ul>
                  <div className="rounded-lg border border-status-info/20 bg-status-info/5 p-3">
                    <p className="text-sm text-muted-foreground">
                      Send yourself a test from the Delivery section before turning a template on, and keep the
                      placeholders intact when you edit.
                    </p>
                  </div>
                </div>
              </CollapsibleContent>
            </Card>
          </Collapsible>
        </SettingsSection>
      </div>
    </div>
  );
};

export default Settings;
